import {
  type SECRGrandTotals,
  type SECRReportResultCategories,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { cn, formatNumber } from '@shared/lib/utils';
import React from 'react';
import { EMISSION_SCOPES } from '../constants';

export type ResultsMetric = 'energy' | 'emissions';

interface SECRReportResultsTableProps {
  metric: ResultsMetric;
  categories: SECRReportResultCategories;
  totals: SECRGrandTotals;
  hasPriorYear: boolean;
  prevYearLabel: string;
  currentYearLabel: string;
}

const fmtCell = (
  value: number | null | undefined,
  row:
    | {
        tonnes?: number | null;
        priorYearTonnes?: number | null;
      }
    | undefined,
  metric: ResultsMetric,
  prior: boolean
) => {
  if (value != null && !isNaN(value)) return formatNumber(value);
  if (metric === 'energy') {
    const tonnes = prior ? row?.priorYearTonnes : row?.tonnes;
    if (tonnes === 0) return formatNumber(0);
  }
  return '-';
};

function pctChange(
  current: number | null | undefined,
  prior: number | null | undefined
) {
  if (current == null || prior == null || prior === 0) return null;
  return ((current - prior) / prior) * 100;
}

function PctBadge({
  current,
  prior,
}: {
  current: number | null | undefined;
  prior: number | null | undefined;
}) {
  const change = pctChange(current, prior);
  if (change == null) return <span className="text-muted-foreground">-</span>;
  const down = change < 0;
  const up = change > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium',
        down
          ? 'text-teal-600'
          : up
            ? 'text-orange-500'
            : 'text-muted-foreground'
      )}
    >
      {down ? (
        <Icons.arrowDown className="size-3" />
      ) : up ? (
        <Icons.arrowUp className="size-3" />
      ) : null}
      {Math.abs(change).toFixed(1)}%
    </span>
  );
}

function getValue(
  cat:
    | {
        tonnes?: number;
        priorYearTonnes?: number | null;
        kwh?: number | null;
        priorYearKwh?: number | null;
      }
    | undefined,
  metric: ResultsMetric,
  prior: boolean
): number | null | undefined {
  if (!cat) return undefined;
  if (metric === 'energy') return prior ? cat.priorYearKwh : cat.kwh;
  return prior ? cat.priorYearTonnes : cat.tonnes;
}

export function SECRReportResultsTable({
  metric,
  categories,
  totals,
  hasPriorYear,
  prevYearLabel,
  currentYearLabel,
}: SECRReportResultsTableProps) {
  const unit = metric === 'energy' ? 'kWh (net)' : 'tCO₂e';
  const sourceLabel = metric === 'energy' ? 'Energy source' : 'Emission source';
  const colSpan = hasPriorYear ? 4 : 2;

  return (
    <div className="overflow-hidden rounded-lg border shadow-sm">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted/50 border-b">
            <th className="p-3 text-left text-sm font-semibold">
              {sourceLabel}
            </th>
            {hasPriorYear && (
              <th className="border-l p-3 text-right text-sm font-semibold">
                {prevYearLabel}
              </th>
            )}
            <th
              className={cn(
                'border-l p-3 text-right text-sm font-semibold',
                hasPriorYear && 'bg-muted/40'
              )}
            >
              {currentYearLabel}
            </th>
            {hasPriorYear && (
              <th className="border-l p-3 text-right text-sm font-semibold">
                % Change
              </th>
            )}
          </tr>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="px-3 py-2 text-left font-normal" />
            {hasPriorYear && (
              <th className="border-l px-3 py-2 text-right font-normal">
                {unit}
              </th>
            )}
            <th
              className={cn(
                'border-l px-3 py-2 text-right font-normal',
                hasPriorYear && 'bg-muted/30'
              )}
            >
              {unit}
            </th>
            {hasPriorYear && (
              <th className="border-l px-3 py-2 text-right font-normal" />
            )}
          </tr>
        </thead>

        <tbody>
          {EMISSION_SCOPES.map(({ label, rows }) => (
            <React.Fragment key={label}>
              <tr className="bg-muted/40 border-b">
                <td
                  colSpan={colSpan}
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {label}
                </td>
              </tr>
              {rows.map(({ key, label: rowLabel, icon }) => {
                const cat = categories[key];
                const current = getValue(cat, metric, false);
                const prior = getValue(cat, metric, true);
                return (
                  <tr
                    key={key}
                    className="hover:bg-muted/20 border-b transition-colors last:border-0"
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {icon && <span className="shrink-0">{icon}</span>}
                        <span className="text-sm">{rowLabel}</span>
                      </div>
                    </td>
                    {hasPriorYear && (
                      <td className="border-l px-3 py-2.5 text-right text-sm tabular-nums">
                        {fmtCell(prior, cat, metric, true)}
                      </td>
                    )}
                    <td
                      className={cn(
                        'border-l px-3 py-2.5 text-right text-sm font-medium tabular-nums',
                        hasPriorYear && 'bg-muted/30'
                      )}
                    >
                      {fmtCell(current, cat, metric, false)}
                    </td>
                    {hasPriorYear && (
                      <td className="border-l px-3 py-2.5 text-right">
                        <PctBadge current={current} prior={prior} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>

        <tfoot className="border-t-2">
          <tr className="bg-muted/40">
            <td className="px-3 py-2.5 text-sm font-semibold">Total</td>
            {hasPriorYear && (
              <td className="border-l px-3 py-2.5 text-right text-sm font-semibold tabular-nums">
                {fmtCell(getValue(totals, metric, true), totals, metric, true)}
              </td>
            )}
            <td
              className={cn(
                'border-l px-3 py-2.5 text-right text-sm font-semibold tabular-nums',
                hasPriorYear && 'bg-muted/30'
              )}
            >
              {fmtCell(getValue(totals, metric, false), totals, metric, false)}
            </td>
            {hasPriorYear && (
              <td className="border-l px-3 py-2.5 text-right">
                <PctBadge
                  current={getValue(totals, metric, false)}
                  prior={getValue(totals, metric, true)}
                />
              </td>
            )}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
